"use client"

import React, { ReactNode } from "react"
import Link from "next/link"

import { formatPrice } from "@/lib/helper"
import { generateProductLink } from "@/lib/link-utils/link-utils"
import { getSbLocaleFromIsoLocale } from "@/lib/locale-helper"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { determinePrefixedProductId } from "@/lib/storefront/product/productHelper"
import { cn } from "@/lib/utils"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"

export default function AccountOrderDetailsLineItemCardClient(props: {
  item: OrderLineItemData
  quantityElement?: ReactNode
  hideSumPrice?: boolean
  isoLocale: string
}) {
  const variantLink = generateProductLink(
    props.item.product?.title,
    props.item.product?.id,
    undefined,
    getSbLocaleFromIsoLocale(props.isoLocale)
  )

  return (
    <div className="col-span-full grid grid-cols-subgrid items-center gap-2 border-b py-2 text-sm last:border-b-0">
      <Link
        prefetch={false}
        className={
          "flex w-fit cursor-pointer flex-col justify-between underline hover:text-primary max-lg:gap-y-2 lg:col-span-2 lg:grid lg:grid-cols-subgrid"
        }
        href={variantLink}
      >
        <div className="flex justify-center">
          <ImageWithFallback
            className={"aspect-square w-10 object-contain lg:w-20"}
            src={props.item.product?.image ?? ""}
            width={80}
            height={80}
            alt={props.item.product?.title ?? ""}
          />
        </div>
        <p className="flex items-center text-xs md:text-sm">
          {determinePrefixedProductId(props.item.product?.id ? (props.item.orderLineItem?.product ?? "") : "")}
        </p>
      </Link>
      <div>
        <p className={"hyphens-auto text-xs max-lg:line-clamp-5 md:text-sm lg:max-w-[48ch] "}>
          {props.item.product?.title ?? props.item.orderLineItem.displayName}
        </p>
      </div>
      <div>{props.quantityElement ?? props.item.orderLineItem.quantity.value + " x"}</div>
      <div className={cn({ "text-right": true, "max-lg:hidden": !props.hideSumPrice })}>
        {(() => {
          try {
            const discounted = props.item.orderLineItem?.pricing?.singleBasePrice?.net
            const undiscounted = props.item.orderLineItem?.pricing?.undiscountedSingleBasePrice?.net
            const dv = Number(discounted?.value ?? 0)
            const uv = Number(undiscounted?.value ?? 0)
            const currency = (discounted?.currency as any) ?? (undiscounted?.currency as any)
            const epsilon = 1e-6
            const showUndiscounted = uv > 0 && (dv + epsilon) < uv
            return showUndiscounted ? (
              <div className="flex flex-col items-end">
                <span className="text-xs text-gray-400 line-through">
                  {formatPrice(
                    props.isoLocale,
                    uv,
                    (undiscounted?.currency as any) ?? currency
                  )}
                </span>
                <span>
                  {formatPrice(props.isoLocale, dv, currency)}
                </span>
              </div>
            ) : (
              formatPrice(props.isoLocale, discounted?.value ?? uv, currency)
            )
          } catch {
            return formatPrice(
              props.isoLocale,
              props.item.orderLineItem?.pricing?.singleBasePrice?.net?.value,
              props.item.orderLineItem?.pricing?.singleBasePrice?.net?.currency
            )
          }
        })()}
      </div>
      {!props.hideSumPrice && (
        <div className="text-right">
          {(() => {
            try {
              const discounted = props.item.orderLineItem?.pricing?.price?.net
              const undiscounted = props.item.orderLineItem?.pricing?.undiscountedPrice?.net
              const dv = Number(discounted?.value ?? 0)
              const uv = Number(undiscounted?.value ?? 0)
              const currency = (discounted?.currency as any) ?? (undiscounted?.currency as any)
              const epsilon = 1e-6
              const showUndiscounted = uv > 0 && (dv + epsilon) < uv
              return showUndiscounted ? (
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-400 line-through">
                    {formatPrice(
                      props.isoLocale,
                      uv,
                      (undiscounted?.currency as any) ?? currency
                    )}
                  </span>
                  <span>
                    {formatPrice(props.isoLocale, dv, currency)}
                  </span>
                </div>
              ) : (
                formatPrice(props.isoLocale, discounted?.value ?? uv, currency)
              )
            } catch {
              return formatPrice(
                props.isoLocale,
                props.item.orderLineItem?.pricing?.price?.net?.value,
                props.item.orderLineItem?.pricing?.price?.net?.currency
              )
            }
          })()}
        </div>
      )}
    </div>
  )
}
